// Batch 009 / ADR-10 — the measurement data layer speaks the NEW contract
// directly (batches/009 measurement.proto + threshold.proto). The legacy BFF
// threshold endpoints that used to live here (/measurement/type,
// /measurement/patient/threshold/*) are GONE — the resolved view IS the type
// catalog (ADR-3: the floor's loinc set is the thresholdable-type set), and
// row ids are no longer part of any contract (the override, not the row, is
// the resource).
//
// The default export keeps its identity (reducerPath 'measurementApi'):
// sidebar.tsx and pages/profile/index.tsx reset it on logout, rootReducer
// registers it, and three batch-007 tests mount it — none of them touch
// endpoints.
import { createApi } from '@reduxjs/toolkit/query/react';

import authedQuery from '@/services/authed.query';
import { patientApi } from '@/services/patient.api';
import {
  AckRequest,
  AckResponse,
  ApiTags,
  FindCalculationsResponse,
  FindMeasurementsResponse,
  LatestSubmissionResponse,
  MeasurementWindowRequest,
  PutThresholdResponse,
  ResolveThresholdsResponse,
  ThresholdBandInput,
  ThresholdKeyRequest,
} from '@/services/api.types';

// The wire requires a non-empty change_reason (threshold.proto PutReq, ADR-3
// validation ⑦) but the FROZEN UI has no such input, so the client sends one
// fixed literal. Persisted into the version lineage; never displayed by this
// app. Single definition point — flagged to main 2026-08-03, swap here if the
// batch rules a different string.
export const THRESHOLD_CHANGE_REASON = 'clinician web override';

export const measurementApi = createApi({
  reducerPath: 'measurementApi',
  tagTypes: [
    ApiTags.PatientThresholdsStatus,
    // Batch 010 / ADR-6: the two window reads below provide this, and the ack
    // mutation invalidates it. Tags are per-api-slice in RTK Query, so this does
    // not interact with the legacy clinician slice's identically-named tag.
    ApiTags.PatientMeasurementStatus,
  ],
  baseQuery: authedQuery,
  endpoints: builder => ({
    // ── read side (ADR-4): two window reads + latest ──
    findMeasurements: builder.query<
      FindMeasurementsResponse,
      MeasurementWindowRequest
    >({
      query: ({ patient, from, to }) => ({
        url: `/v1/patients/${patient}/measurements`,
        params: { from, to },
      }),
      providesTags: (result, error, { patient }) => [
        { type: ApiTags.PatientMeasurementStatus, id: patient },
      ],
    }),
    findCalculations: builder.query<
      FindCalculationsResponse,
      MeasurementWindowRequest
    >({
      query: ({ patient, from, to }) => ({
        url: `/v1/patients/${patient}/calculations`,
        params: { from, to },
      }),
      providesTags: (result, error, { patient }) => [
        { type: ApiTags.PatientMeasurementStatus, id: patient },
      ],
    }),
    findLatestMeasurement: builder.query<
      LatestSubmissionResponse,
      { patient: string }
    >({
      query: ({ patient }) => ({
        url: `/v1/patients/${patient}/measurements/latest`,
      }),
    }),

    // ── threshold resolved view + write-through override (ADR-3) ──
    resolveThresholds: builder.query<ResolveThresholdsResponse, string>({
      query: patient => ({
        url: `/v1/patients/${patient}/thresholds`,
      }),
      providesTags: (result, error, patient) => [
        { type: ApiTags.PatientThresholdsStatus, id: patient },
      ],
    }),
    putThreshold: builder.mutation<
      PutThresholdResponse,
      ThresholdKeyRequest & { bands: ThresholdBandInput[] }
    >({
      query: ({ patient, loinc, bands }) => ({
        url: `/v1/patients/${patient}/thresholds/${loinc}`,
        method: 'PUT',
        // snake_case body — proto field names (requests are snake, responses
        // camel: the repo-wide protojson rule).
        body: { bands, change_reason: THRESHOLD_CHANGE_REASON },
      }),
      // Invalidate UNCONDITIONALLY (also on error): the BP visual row is two
      // non-atomic PUTs (rule ④) — after a partial failure the server holds a
      // state the FE never wrote, and converging means refetching the resolved
      // view. RTK evaluates invalidatesTags for rejected mutations too, so
      // returning the tag on the error path is the whole "forced refetch".
      invalidatesTags: (result, error, { patient }) => [
        { type: ApiTags.PatientThresholdsStatus, id: patient },
      ],
    }),
    // ── acknowledgement (batch 010 / ADR-6, contract Delta 1) ──
    //
    // ONE idempotent operation, first-wins on the server: re-acknowledging is a
    // 200 no-op, never a conflict, so there is deliberately NO 409 branch at the
    // call site (the legacy surface had one; ADR-6 records its removal as an
    // intended behaviour change).
    //
    // The path avoids the /v1/patients/{patient}/… subtree on purpose — kratos
    // registers routes in proto declaration order with no literal-first rule.
    ackMeasurements: builder.mutation<AckResponse, AckRequest>({
      query: body => ({
        url: '/v1/measurements/ack',
        method: 'POST',
        // snake_case body — proto field names (requests are snake, responses
        // camel: the repo-wide protojson rule). No acknowledger and no
        // timestamp: the server takes the caller from the token and stamps the
        // time itself, and both legacy inputs were BFF-era artefacts.
        body,
      }),
      // Acknowledging changes what the two window reads return, and the ack
      // state has to become visible without a manual reload. Invalidated WITHOUT
      // an id, which in RTK Query matches every entry providing this type — the
      // mutation does not carry a patient key, and one clinician's panel only
      // ever holds one patient's windows.
      invalidatesTags: [ApiTags.PatientMeasurementStatus],
      // PD-2952: acknowledging also changes what the pinned strip shows
      // (`last_alarm` empties, `all_measurement_is_ack` flips), and the strip's
      // query lives in the PATIENT slice. RTK Query tags never cross slice
      // boundaries — the legacy single-api `ackMeasurements` invalidated
      // `PatientPinnedStatus` and that edge was lost in the split — so the
      // invalidation is dispatched at the other slice explicitly. Only after
      // `queryFulfilled`: a rejected ack changed nothing server-side, so nothing
      // may refetch here (and the await must be caught — an uncaught one takes
      // the process down, batch 016's measurement).
      //
      // ⚠️ Deliberately ONLY the pinned strip. ADR-B5's Skeptic keeps the paged
      // patient list un-invalidated by the pin toggle and this ack (transfer
      // writes do expire it, PD-3033); do not add PatientList here.
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        dispatch(
          patientApi.util.invalidateTags([
            { type: ApiTags.PatientPinnedStatus, id: 'LIST' },
          ])
        );
      },
    }),
    deleteThreshold: builder.mutation<void, ThresholdKeyRequest>({
      query: ({ patient, loinc }) => ({
        url: `/v1/patients/${patient}/thresholds/${loinc}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { patient }) => [
        { type: ApiTags.PatientThresholdsStatus, id: patient },
      ],
    }),
  }),
});

export const {
  useFindMeasurementsQuery,
  useFindCalculationsQuery,
  useFindLatestMeasurementQuery,
  useResolveThresholdsQuery,
  usePutThresholdMutation,
  useDeleteThresholdMutation,
  useAckMeasurementsMutation,
} = measurementApi;

export default measurementApi;
